import React, { useState } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import {
    Button,
    Grid,
    TextField,
    Paper,
    Box,
    Typography,
    Card,
    CardContent,
    CardHeader,
    Chip
} from '@mui/material';
import { createTheme } from "@mui/material/styles";
import { styled } from "@mui/material/styles";
import FactCheckIcon from '@mui/icons-material/FactCheck';
import { getJobOrders } from '../API/jobOrderAPI';
import BaseFormWrapper from "../../components/shared/BaseFormWrapper";
import { fetchAndFilterJOsBySingleDate } from '../JobOrders/utils/JobOrderFilter';
import { groupByTechnicianGroupsForCreation } from './utils/EndorsementGrouping';

const theme = createTheme({
    palette: {
        primary: {
            main: "#1e5d45", // Custom primary color
        },
    },
});

const StyledHr = styled("hr")(({ theme }) => ({
    border: 0,
    borderTop: `1px solid ${theme.palette.divider}`,
    // margin: theme.spacing(4, 0),
    opacity: 0.8,
}));


function CreateEndorsementForm() {
    const [dateFilter, setDateFilter] = useState();
    const [groupedList, setGroupedList] = useState([]);
    const navigate = useNavigate();
    const [isLoading, setIsLoading] = useState(false);
    const [successMessage, setSuccessMessage] = useState("");
    const [errorMessage, setErrorMessage] = useState("");

    const handleSearchButtonClick = async () => {
        setIsLoading(true)

        // Use the shared single-date helper (already applies 05:00 → next-day 04:59 window)
        const { list: transformedResults } = await fetchAndFilterJOsBySingleDate({
            date: dateFilter,
            status: [],                // include all statuses
            serviceReportStatus: [],   // include all SR statuses
            billingRecordStatus: [],   // include all billing statuses
            // If need to scope to a technician, also pass: userRoles, technicianId
            getJobOrdersApi: getJobOrders,
        });

        const groups = groupByTechnicianGroupsForCreation(transformedResults);
        setGroupedList(groups);
        setIsLoading(false)
    };

    const handleSelectGroup = (group) => {
        console.log(group);
        // Navigate to the submit-endorsement-form route with state
        navigate('/submit-endorsement-form', { state: { group } });
    };

    return (
        <BaseFormWrapper
            titleIcon={<FactCheckIcon />}
            title="Operations"
            subTitle="Manage Endorsement Form"
            subTitleRoute="/manage-endorsement-form"
            formTitle="Create Endorsement Form"
            successMessage={successMessage}
            setSuccessMessage={setSuccessMessage}
            errorMessage={errorMessage}
            setErrorMessage={setErrorMessage}
            isLoading={isLoading}
        >

            <Grid item xs={12}>
                <Grid container spacing={2} >
                    <Grid item xs={12}>
                        <Grid container spacing={2} alignItems="flex-end">
                            <Grid item xs={4}>
                                <TextField
                                    label="Date"
                                    name="date_filter"
                                    type="date"
                                    value={dateFilter || ''}
                                    onChange={(e) => setDateFilter(e.target.value)}
                                    InputLabelProps={{ shrink: true, }}
                                    fullWidth
                                />
                            </Grid>
                            <Grid item xs={3}>
                                <Button
                                    variant="contained"
                                    color="primary"
                                    onClick={handleSearchButtonClick} // Call button handler
                                >
                                    Search
                                </Button>
                            </Grid>
                        </Grid>
                    </Grid>
                </Grid>

                <StyledHr />

                <Grid item xs={12}>
                    {groupedList.map((group, index) => (
                        <Card key={index} >
                            <CardContent>
                                <Grid container spacing={2} alignItems="center">
                                    {/* Technician Section */}
                                    <Grid
                                        item
                                        xs={12}
                                        md={12}
                                        sx={{ display: "flex", flexDirection: "column" }}
                                    >
                                        <Grid container spacing={2}>
                                            <Grid item xs={12} md={4}>
                                                <CardHeader
                                                    title={
                                                        <Typography
                                                            variant="h6"
                                                        >
                                                            Technicians
                                                        </Typography>
                                                    }
                                                />
                                                <Grid container spacing={2}>
                                                    {group.technicians.length > 0 ? (
                                                        group.technicians.map((tech, idx) => (
                                                            <Grid item xs={12} key={idx}>
                                                                <Paper
                                                                    variant="outlined"
                                                                    sx={{
                                                                        p: 2,
                                                                        textAlign: 'center',
                                                                        borderRadius: 2,
                                                                        backgroundColor: '#fafafa',
                                                                    }}
                                                                >
                                                                    <Typography variant="subtitle1">
                                                                        {tech.firstname} {tech.lastname}
                                                                    </Typography>
                                                                </Paper>
                                                            </Grid>
                                                        ))
                                                    ) : (
                                                        <Grid item xs={12}>
                                                            <Paper
                                                                variant="outlined"
                                                                sx={{
                                                                    p: 2,
                                                                    textAlign: 'center',
                                                                    borderRadius: 2,
                                                                    backgroundColor: '#fafafa',
                                                                }}
                                                            >
                                                                <Typography variant="subtitle1">No technician found</Typography>
                                                            </Paper>
                                                        </Grid>
                                                    )}
                                                </Grid>

                                                {/* Total Job Orders Card (per group) */}
                                                <Grid item xs={12} sx={{ mt: 1 }}>
                                                    <Card
                                                        sx={{
                                                        backgroundColor: '#1976D2',
                                                        color: '#FFF',
                                                        borderRadius: 1,
                                                        textAlign: 'center',
                                                        padding: '4px',
                                                        boxShadow: 1,
                                                        }}
                                                    >
                                                        <CardContent sx={{ padding: '4px !important' }}>
                                                        <Typography variant="body2" sx={{ fontWeight: 'bold', fontSize: '0.875rem' }}>
                                                            Total Job Orders
                                                        </Typography>
                                                        <Typography variant="h5" sx={{ fontSize: '1.5rem' }}>
                                                            {Array.isArray(group.jobOrders) ? group.jobOrders.length : 0}
                                                        </Typography>
                                                        </CardContent>
                                                    </Card>
                                                </Grid>
                                            </Grid>                                          

                                            {/* Job Orders Section */}
                                            <Grid item xs={12} md={8}>
                                                <CardHeader
                                                    title={
                                                        <Typography
                                                            variant="h6"
                                                        >
                                                            Job Orders
                                                        </Typography>
                                                    }
                                                />
                                                {group.jobOrders.map((jobOrder, idx) => (
                                                    <Card key={idx} sx={{ borderRadius: 2, backgroundColor: '#fafafa' }}>
                                                        <CardContent>
                                                            <Grid container spacing={2}>
                                                                {/* Client Information */}
                                                                <Grid item xs={12}>
                                                                    <Typography variant="subtitle1" fontWeight="bold">
                                                                        Client: {jobOrder.client?.name || 'No client found'}
                                                                    </Typography>
                                                                    <Typography variant="body2" color="textSecondary">
                                                                        Address: {`${jobOrder.client?.address?.house_unit_street || 'No address'}, ${jobOrder.client?.address?.barangay || ''}, ${jobOrder.client?.address?.city || ''}, ${jobOrder.client?.address?.metro_province || ''} ${jobOrder.client?.address?.zipcode || ''}`}
                                                                    </Typography>
                                                                    <Typography variant="body2" color="textSecondary">
                                                                        Contact: {`${jobOrder.client?.contact?.firstname || ''} ${jobOrder.client?.contact?.lastname || ''}`}
                                                                    </Typography>
                                                                    <Typography variant="body2" color="textSecondary">
                                                                        Number: {jobOrder.client?.contact?.numbers?.[0]?.number || 'No number available'}
                                                                    </Typography>
                                                                    <Typography variant="body2" color="textSecondary">
                                                                        Email: {jobOrder.client?.contact?.emails?.[0]?.email || 'No email available'}
                                                                    </Typography>
                                                                </Grid>

                                                                {/* Schedule */}
                                                                <Grid item xs={12}>
                                                                    <Typography variant="subtitle1" fontWeight="bold">
                                                                        Schedule:
                                                                    </Typography>
                                                                    <Typography variant="body2" color="textSecondary">
                                                                        Date: {jobOrder.job_date || 'No job date'}
                                                                    </Typography>
                                                                    <Typography variant="body2" color="textSecondary">
                                                                        Start Time: {jobOrder.start_time || 'No start time'}
                                                                    </Typography>
                                                                    <Typography variant="body2" color="textSecondary">
                                                                        End Time: {jobOrder.end_time || 'No end time'}
                                                                    </Typography>
                                                                </Grid>                                                                

                                                                {/* Job Type (replaces Scope) */}
                                                                <Grid item xs={12}>
                                                                    <Typography variant="subtitle1" fontWeight="bold">
                                                                        Job Type:
                                                                    </Typography>
                                                                    {Array.isArray(jobOrder.job_types) && jobOrder.job_types.length > 0 ? (
                                                                        <Box
                                                                        sx={{
                                                                            display: "flex",
                                                                            flexWrap: "wrap",
                                                                            gap: 1,
                                                                        }}
                                                                        >
                                                                        {jobOrder.job_types.map((type, i) => (
                                                                            <Chip
                                                                            key={i}
                                                                            label={type.replace(/_/g, " ")}
                                                                            variant="outlined"
                                                                            size="small"
                                                                            />
                                                                        ))}
                                                                        </Box>
                                                                    ) : (
                                                                        <Typography variant="body2" color="textSecondary">
                                                                        No job types available
                                                                        </Typography>
                                                                    )}
                                                                </Grid>
                                                            </Grid>
                                                        </CardContent>
                                                    </Card>
                                                ))}
                                            </Grid>
                                        </Grid>

                                        {/* Select Button */}
                                        <Grid item xs={12} sx={{ textAlign: 'right' }}>
                                            <Button
                                                variant="contained"
                                                color="primary"
                                                size="small"
                                                sx={{ borderRadius: 2 }}
                                                onClick={() => handleSelectGroup(group)}
                                            >
                                                Select
                                            </Button>
                                        </Grid>

                                    </Grid>
                                </Grid>
                            </CardContent>
                        </Card>
                    ))}
                </Grid>
            </Grid>
        </BaseFormWrapper>        
    );
}

export default CreateEndorsementForm;
